<script lang="ts">
  import { Status } from '@do/models';
  import { DirectusImage, Preset } from '@do/svelte-directus-images';

  import { Accordion } from '@shared/ui/accordion';
  import { AvatarPlaceholder } from '@shared/ui/avatar-placeholder';
  import { Icon } from '@shared/ui/icon';
  import { buildDataItemsParam } from '@shared/ui/tabs';
  import { cn, isSafari } from '@shared/utils';

  import type { ArtAsset } from '@entities/art-asset';
  import { purchaseModel, PurchaseState } from '@entities/purchase';

  import { ArtworkDoModal } from '@features/artwork/artwork-do-modal';
  import { artworkPurchaseInfo } from '@features/purchase/purchase-artwork';
  import { requestAccordionActivity } from '@features/purchase/purchase-request';

  import { getTitleSizeClass } from '../libs';

  export let artAsset: ArtAsset;

  $: styles = artAsset?.styles ? buildDataItemsParam(artAsset.styles) : null;
  $: medium = artAsset?.medium ? buildDataItemsParam(artAsset.medium) : null;
  $: isSafariBrowser = isSafari();
  $: isProcessing =
    $purchaseModel.state === PurchaseState.Pending ||
    $purchaseModel.state === PurchaseState.InProgress ||
    $purchaseModel.state === PurchaseState.Approval;
  $: isSafariPurchaseProcessing = isProcessing && isSafariBrowser;
</script>

<article class="mb-3 flex flex-wrap gap-3 gap-x-5 gap-y-3">
  {#if artAsset.artists?.length}
    {#each artAsset.artists as artist}
      <svelte:element
        this="{artist.status === Status.Published ? 'a' : 'div'}"
        href="/artist-{artist.id}-{artist.slug}/"
        class="flex items-center gap-3"
      >
        <div class="h-8 w-8 shrink-0 overflow-hidden rounded-full">
          <DirectusImage
            imageData="{artist.photo}"
            alt="{artist.name}"
            pictureSizes="{{
              default: '32px',
            }}"
            presetKeys="{Preset.Mini}"
            loading="lazy"
            imgClasses="w-full h-full object-cover"
          >
            <AvatarPlaceholder slot="default-placeholder" rounded name="{artist.name}" />
          </DirectusImage>
        </div>

        <span class="font-neue text-base font-bold md:text-xl">{artist.name}</span>
      </svelte:element>
    {/each}
  {/if}
</article>

<h1
  class="{cn(
    'hover:text-default-700 dark:hover:text-default-400 mb-5 font-medium transition-colors',
    getTitleSizeClass('June 21, 2022 Day 118 A Fragment of the Map with the Border of Lithuania and the Kalining'),
  )}"
>
  {artAsset.name_english}
</h1>

<Accordion isOpen="{$requestAccordionActivity && !$artworkPurchaseInfo && !isSafariPurchaseProcessing}">
  <p class="mb-6 text-base">
    {#if artAsset.creation_year}
      {artAsset.creation_year},
    {/if}

    {#if artAsset.medium.length}
      {medium}
    {/if}

    {#if artAsset.styles.length}
      , {styles}
    {/if}
  </p>

  <div class="mb-2.5">
    <ArtworkDoModal digitalOriginalData="{artAsset.digital_original}" hasLabel />
  </div>

  <div class="mb-5 flex max-w-[416px] gap-2 text-sm lg:max-w-full">
    <div>
      <Icon name="info" width="{18}" height="{18}" />
    </div>

    <div>
      This is a <strong>Digital Original</strong> of the artwork, digitized <br class="hidden xl:block" /> using <ArtworkDoModal
        digitalOriginalData="{artAsset.digital_original}"
        variant="tertiary"
      >
        <div
          class="dark:hover:text-default-300 hover:text-default-500 flex items-center gap-1 font-medium transition-colors"
        >
          <strong class="underline">DLCT</strong>
        </div>
      </ArtworkDoModal> technology, and <strong>exists as a unique exemplar.</strong> <br />
      Each Digital Original is <strong>verified and secured </strong> through blockchain technology, providing
      <strong>full rights to exclusive digital ownership.</strong>
    </div>
  </div>
</Accordion>
